import { IconType } from 'react-icons';
import { MdFormatBold, MdFormatItalic, MdFormatListBulleted, MdFormatListNumbered, MdLink } from 'react-icons/md';
import { useIntl } from 'react-intl';

export type MarkdownAction = 'bold' | 'italic' | 'bulletList' | 'numberedList' | 'link';

export interface MarkdownEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

const wrap = (value: string, start: number, end: number, before: string, after: string): MarkdownEdit => ({
  value: value.slice(0, start) + before + value.slice(start, end) + after + value.slice(end),
  selectionStart: start + before.length,
  selectionEnd: end + before.length,
});

const prefixLines = (value: string, start: number, end: number, prefix: (index: number) => string): MarkdownEdit => {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = value.indexOf('\n', end) === -1 ? value.length : value.indexOf('\n', end);
  const lines = value.slice(lineStart, lineEnd).split('\n').map((line, index) => prefix(index) + line);
  const replaced = lines.join('\n');

  return {
    value: value.slice(0, lineStart) + replaced + value.slice(lineEnd),
    selectionStart: lineStart + prefix(0).length,
    selectionEnd: lineStart + replaced.length,
  };
};

export function applyMarkdown(action: MarkdownAction, value: string, start: number, end: number): MarkdownEdit {
  switch (action) {
    case 'bold':
      return wrap(value, start, end, '**', '**');
    case 'italic':
      return wrap(value, start, end, '*', '*');
    case 'bulletList':
      return prefixLines(value, start, end, () => '- ');
    case 'numberedList':
      return prefixLines(value, start, end, index => `${index + 1}. `);
    case 'link': {
      const url = 'https://';
      const edit = wrap(value, start, end, '[', `](${url})`);
      // With selected text the user still needs the URL; without it they need the link text first.
      return start === end ? edit : { ...edit, selectionStart: end + 3, selectionEnd: end + 3 + url.length };
    }
    default:
      return { value, selectionStart: start, selectionEnd: end };
  }
}

const buttons: { action: MarkdownAction, icon: IconType, labelId: string }[] = [
  { action: 'bold', icon: MdFormatBold, labelId: 'markdownBold' },
  { action: 'italic', icon: MdFormatItalic, labelId: 'markdownItalic' },
  { action: 'bulletList', icon: MdFormatListBulleted, labelId: 'markdownBulletList' },
  { action: 'numberedList', icon: MdFormatListNumbered, labelId: 'markdownNumberedList' },
  { action: 'link', icon: MdLink, labelId: 'markdownLink' },
];

interface MarkdownToolbarProps {
  onAction: (action: MarkdownAction) => void;
}

export default function MarkdownToolbar({ onAction }: MarkdownToolbarProps) {
  const { formatMessage } = useIntl();

  return (
    <div role="toolbar" aria-label={formatMessage({ id: 'markdownToolbar' })} className="flex gap-1 mb-1">
      {buttons.map(({ action, icon: Icon, labelId }) => (
        <button
          key={action}
          type="button"
          aria-label={formatMessage({ id: labelId })}
          title={formatMessage({ id: labelId })}
          onClick={() => onAction(action)}
          className="p-1 rounded text-gray-600 hover:bg-lightGray focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <Icon size={20} aria-hidden />
        </button>
      ))}
    </div>
  );
}
